// data/items/woodworking.js
// Woodworking items: arrow shafts, arrows, and bows

export const ARROW_SHAFTS = [
    {
        id: 'arrow_shafts',
        name: 'Arrow Shafts',
        color: '#8B4513',
        value: 2,
        sprite: { sheet: 'assets/icons/woodworking/arrow_shaft_1.png', x: 0, y: 0, width: 64, height: 64 },
        sprites: [
            { minQty: 1, sheet: 'assets/icons/woodworking/arrow_shaft_1.png', x: 0, y: 0, width: 64, height: 64 },
            { minQty: 10, sheet: 'assets/icons/woodworking/arrow_shaft_2.png', x: 0, y: 0, width: 64, height: 64 },
            { minQty: 50, sheet: 'assets/icons/woodworking/arrow_shaft_3.png', x: 0, y: 0, width: 64, height: 64 },
            { minQty: 250, sheet: 'assets/icons/woodworking/arrow_shaft_4.png', x: 0, y: 0, width: 64, height: 64 },
            { minQty: 1000, sheet: 'assets/icons/woodworking/arrow_shaft_5.png', x: 0, y: 0, width: 64, height: 64 },
        ]
    }
];

// Arrow icons swap to a fuller-stack sprite (_2) at 250+, matching the
// quantity-tier pattern used by coins (misc.js) and arrow shafts above.
function arrowSprites(prefix) {
    return {
        sprite: { sheet: `assets/icons/woodworking/${prefix}_arrows_1.png`, x: 0, y: 0, width: 64, height: 64 },
        sprites: [
            { minQty: 1, sheet: `assets/icons/woodworking/${prefix}_arrows_1.png`, x: 0, y: 0, width: 64, height: 64 },
            { minQty: 250, sheet: `assets/icons/woodworking/${prefix}_arrows_2.png`, x: 0, y: 0, width: 64, height: 64 }
        ]
    };
}

export const ARROWS = [
    {
        id: 'pyrestone_arrows',
        name: 'Pyrestone Arrows',
        color: '#b87333',
        value: 15,
        slot: 'ammo',
        attackBonus: 2,
        rangedAccuracy: 3,
        combatLevel: 1,
        ...arrowSprites('pyrestone')
    },
    {
        id: 'pale_shard_arrows',
        name: 'Pale Shard Arrows',
        color: '#c0c0c0',
        value: 30,
        slot: 'ammo',
        attackBonus: 4,
        rangedAccuracy: 5,
        combatLevel: 15,
        ...arrowSprites('pale_shard')
    },
    {
        id: 'blackiron_arrows',
        name: 'Blackiron Arrows',
        color: '#696969',
        value: 52,
        slot: 'ammo',
        attackBonus: 7,
        rangedAccuracy: 8,
        combatLevel: 30,
        ...arrowSprites('blackiron')
    },
    {
        id: 'skyiron_arrows',
        name: 'Skyiron Arrows',
        color: '#4169e1',
        value: 82,
        slot: 'ammo',
        attackBonus: 11,
        rangedAccuracy: 12,
        combatLevel: 45,
        ...arrowSprites('skyiron')
    },
    {
        id: 'verdant_core_arrows',
        name: 'Verdant Core Arrows',
        color: '#228b22',
        value: 120,
        slot: 'ammo',
        attackBonus: 16,
        rangedAccuracy: 17,
        combatLevel: 60,
        ...arrowSprites('verdant_core')
    },
    {
        id: 'voidsteel_arrows',
        name: 'Voidsteel Arrows',
        color: '#00ced1',
        value: 165,
        slot: 'ammo',
        attackBonus: 22,
        rangedAccuracy: 23,
        combatLevel: 75,
        ...arrowSprites('voidsteel')
    }
];

// Bow tier definitions with combat stats
// Scaled to max combat level 75
const BOW_TIERS = {
    1: { prefix: '', color: '#8B4513', attack: 6, speed: 3600, accuracy: 6, combatLevel: 1, value: 70 },
    2: { prefix: 'Ironwood', color: '#A0522D', attack: 12, speed: 3400, accuracy: 12, combatLevel: 15, value: 160 },
    3: { prefix: 'Softbark', color: '#6B8E23', attack: 22, speed: 3100, accuracy: 18, combatLevel: 30, value: 300 },
    4: { prefix: 'Goldleaf', color: '#D2691E', attack: 36, speed: 2900, accuracy: 24, combatLevel: 45, value: 510 },
    5: { prefix: 'Redgrain', color: '#d2691e', attack: 46, speed: 2600, accuracy: 30, combatLevel: 55, value: 650 },
    6: { prefix: 'Gravewood', color: '#228b22', attack: 58, speed: 2350, accuracy: 36, combatLevel: 65, value: 900 },
    7: { prefix: 'Arcanewood', color: '#9932CC', attack: 75, speed: 2100, accuracy: 44, combatLevel: 75, value: 1300 }
};

export const BOWS = [
    // Tier 1: Basic bow
    {
        id: 'shortbow',
        name: 'Shortbow',
        color: BOW_TIERS[1].color,
        tier: 1,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: BOW_TIERS[1].attack,
        attackSpeed: BOW_TIERS[1].speed,
        rangedAccuracy: BOW_TIERS[1].accuracy,
        combatLevel: BOW_TIERS[1].combatLevel,
        value: BOW_TIERS[1].value
    },
    // Tier 2: Ironwood
    {
        id: 'ironwood_shortbow',
        name: 'Ironwood Shortbow',
        color: BOW_TIERS[2].color,
        tier: 2,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: BOW_TIERS[2].attack,
        attackSpeed: BOW_TIERS[2].speed,
        rangedAccuracy: BOW_TIERS[2].accuracy,
        combatLevel: BOW_TIERS[2].combatLevel,
        value: BOW_TIERS[2].value
    },
    // Tier 3: Softbark
    {
        id: 'softbark_shortbow',
        name: 'Softbark Shortbow',
        color: BOW_TIERS[3].color,
        tier: 3,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: BOW_TIERS[3].attack,
        attackSpeed: BOW_TIERS[3].speed,
        rangedAccuracy: BOW_TIERS[3].accuracy,
        combatLevel: BOW_TIERS[3].combatLevel,
        value: BOW_TIERS[3].value
    },
    // Tier 4: Goldleaf
    {
        id: 'goldleaf_shortbow',
        name: 'Goldleaf Shortbow',
        color: BOW_TIERS[4].color,
        tier: 4,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: BOW_TIERS[4].attack,
        attackSpeed: BOW_TIERS[4].speed,
        rangedAccuracy: BOW_TIERS[4].accuracy,
        combatLevel: BOW_TIERS[4].combatLevel,
        value: BOW_TIERS[4].value
    },
    // Tier 5: Redgrain
    {
        id: 'redgrain_shortbow',
        name: 'Redgrain Shortbow',
        color: BOW_TIERS[5].color,
        tier: 5,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: BOW_TIERS[5].attack,
        attackSpeed: BOW_TIERS[5].speed,
        rangedAccuracy: BOW_TIERS[5].accuracy,
        combatLevel: BOW_TIERS[5].combatLevel,
        value: BOW_TIERS[5].value
    },
    // Tier 6: Gravewood
    {
        id: 'gravewood_shortbow',
        name: 'Gravewood Shortbow',
        color: BOW_TIERS[6].color,
        tier: 6,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: BOW_TIERS[6].attack,
        attackSpeed: BOW_TIERS[6].speed,
        rangedAccuracy: BOW_TIERS[6].accuracy,
        combatLevel: BOW_TIERS[6].combatLevel,
        value: BOW_TIERS[6].value
    },
    // Tier 7: Arcanewood
    {
        id: 'arcanewood_shortbow',
        name: 'Arcanewood Shortbow',
        color: BOW_TIERS[7].color,
        tier: 7,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: BOW_TIERS[7].attack,
        attackSpeed: BOW_TIERS[7].speed,
        rangedAccuracy: BOW_TIERS[7].accuracy,
        combatLevel: BOW_TIERS[7].combatLevel,
        value: BOW_TIERS[7].value
    }
];

// HD icons — filename matches bow id (assets/icons/equipment/rangedWeapons/{id}.png)
for (const bow of BOWS) {
    bow.sprite = { sheet: `assets/icons/equipment/rangedWeapons/${bow.id}.png`, x: 0, y: 0, width: 64, height: 64 };
}

export default {
    ARROW_SHAFTS,
    ARROWS,
    BOWS
};
